/**
 * Team Trips — Intersection Observer Alpine component
 *
 * Watches each builder section as it enters the viewport and calls
 * `$wire.recordStepViewed(stepNumber)` via Alpine's `$wire` magic so the
 * Livewire component can track which steps the user has seen (for analytics
 * and sidebar progress highlighting).
 *
 * rootMargin: '-80px 0px -55% 0px' matches SPEC_steps_1_through_6.md §A7:
 *   - Top offset 80px = sticky nav height, so a step is "seen" once it clears the nav.
 *   - Bottom offset -55% = step is considered active when its top half is visible.
 *
 * Usage in Blade (on the Livewire component root):
 *   <div x-data="stepObserver" x-init="observe">
 *     <section id="step-1" data-step="1">...</section>
 *     <section id="step-2" data-step="2">...</section>
 *     ...
 *   </div>
 *
 * Each observed section must carry a `data-step` attribute containing the
 * 1-based step number.
 */
export default function stepObserver() {
    return {
        /** Internal IntersectionObserver reference for teardown. */
        _observer: null,

        observe() {
            /* Guard: IntersectionObserver may not exist in very old browsers */
            if (typeof IntersectionObserver === 'undefined') {
                return;
            }

            this._observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (!entry.isIntersecting) {
                            return;
                        }

                        const stepNumber = parseInt(entry.target.dataset.step, 10);

                        if (!Number.isFinite(stepNumber)) {
                            return;
                        }

                        /* Alpine $wire magic calls the Livewire method. */
                        if (this.$wire) {
                            this.$wire.recordStepViewed(stepNumber);
                            /* Unobserve after first fire — prevents duplicate analytics
                               events if user scrolls back up through the same step. */
                            this._observer.unobserve(entry.target);
                        }
                    });
                },
                {
                    rootMargin: '-80px 0px -55% 0px',
                }
            );

            /* Observe every section inside this component that has data-step. */
            this.$el.querySelectorAll('[data-step]').forEach((section) => {
                this._observer.observe(section);
            });
        },

        destroy() {
            if (this._observer !== null) {
                this._observer.disconnect();
                this._observer = null;
            }
        },
    };
}
